import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { useVideo } from "../providers/VideoContext";
import { Button } from "@themes/pollinator/ui/Button";
import { Dialog } from "@themes/pollinator/ui/Dialog";
import { Type } from "@themes/pollinator/ui/Type";
import { useCustomMeetingManager } from "../hooks/useCustomMeetingManager";
import { extractRoomMemberData } from "../utils/roomMemberDataUtils";
import { useBrowserCompatibilityFeatures } from "../hooks/useBrowserCompatibilityFeatures";
import { useDevices } from "../providers/DevicesProvider";
import { logVideoRoomError } from "../utils/api/videoRoomErrorLog";

// Helper function to detect iOS devices
const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
};

type Props = {
  meetingId: string | number;
  isActionLoading: boolean;
  isDevicesLoading: boolean;
  onJoinMeeting?: () => Promise<void>;
};

const MeetingJoinDetails = ({
  meetingId,
  isActionLoading,
  isDevicesLoading,
  onJoinMeeting,
}: Props) => {
  const {
    joinMeeting,
    error: meetingError,
    roomAccess,
  } = useCustomMeetingManager();

  const { isUserSupported } = useBrowserCompatibilityFeatures();
  const { selectedAudioOutputDevice, audioOutputDevices } = useDevices();

  const [isLoading, setIsLoading] = useState(false);
  const [showNoSpeakersMessage, setShowNoSpeakersMessage] = useState(false);

  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  const { userName, roomMembersData } = useVideo();

  // Get roomId from roomAccess if available
  const roomId = roomAccess?.room_id;

  // Extract display name from userName (which is in externalUserId format)
  const { displayName } = userName
    ? extractRoomMemberData({ externalUserId: userName }, roomMembersData)
    : { displayName: "Participant" };

  // Check if devices are ready (not loading - allow joining even without devices)
  const areDevicesReady = !isDevicesLoading;
  
  // Check if speaker source is selected (for display purposes only - not used to disable join)
  const isIOS = isIOSDevice();
  const hasSpeakerSelected = isIOS || selectedAudioOutputDevice !== null;

  useEffect(() => {
    if (meetingError) {
      setIsErrorDialogOpen(true);
    }
  }, [meetingError]);

  // Handle delayed display of no speakers message
  useEffect(() => {
    // Skip message for iOS devices due to known speaker detection limitations
    if (isIOS) {
      setShowNoSpeakersMessage(false);
      return;
    }

    // Reset the message when devices are loading
    if (isDevicesLoading) {
      setShowNoSpeakersMessage(false);
      return;
    }

    // If devices are not loading and we have no speakers, show message after a delay
    if (!isDevicesLoading && audioOutputDevices.length === 0 && !hasSpeakerSelected) {
      const timer = setTimeout(() => {
        setShowNoSpeakersMessage(true);
        
        // Log the error for analytics and debugging
        if (roomId) {
          logVideoRoomError(
            roomId,
            "No speaker devices detected - user cannot select audio output",
            "no_speakers_detected",
            {
              audio_output_devices_count: audioOutputDevices.length,
              is_devices_loading: isDevicesLoading,
              has_speaker_selected: hasSpeakerSelected,
              is_ios_device: isIOS,
              user_agent: navigator.userAgent,
              timestamp: new Date().toISOString()
            }
          ).catch(error => {
            console.warn('Failed to log video room error:', error);
          });
        }
      }, 2000); // 2 second delay

      return () => clearTimeout(timer);
    } else {
      // If we have speakers or a speaker is selected, hide the message
      setShowNoSpeakersMessage(false);
    }
  }, [isDevicesLoading, audioOutputDevices.length, hasSpeakerSelected, isIOS, roomId]);

  const handleJoinMeeting = async () => {
    setIsLoading(true);
    setIsErrorDialogOpen(false);

    try {
      onJoinMeeting?.();

      await joinMeeting(roomId);
    } catch (error) {
      setIsErrorDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Determine what to display based on whether we're joining a room or direct meeting
  const getDisplayInfo = () => {
    // console.log("roomId", roomId);
    // console.log("roomAccess", roomAccess);
    if (roomId && roomAccess) {
      return {
        title: roomAccess.title,
        subtitle: `Joining room as ${displayName}`,
        buttonText: "Join Meeting",
      };
    } else if (meetingId) {
      return {
        title: `Meeting ID: ${meetingId}`,
        subtitle: `Joining meeting as ${displayName}`,
        buttonText: "Join Meeting",
      };
    } else {
      return {
        title: "New Meeting",
        subtitle: `Creating new meeting as ${displayName}`,
        buttonText: "Create Meeting",
      };
    }
  };

  const displayInfo = getDisplayInfo();

  // Determine button text based on state
  const getButtonText = () => {
    if (isLoading) return "Loading...";
    if (isDevicesLoading) return "Loading Devices...";
    if (!areDevicesReady) return "Loading...";
    if (isActionLoading) return "Loading...";

    return displayInfo.buttonText;
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <Button
          onClick={handleJoinMeeting}
          disabled={
            isLoading ||
            isDevicesLoading ||
            !areDevicesReady ||
            isActionLoading ||
            !isUserSupported
          }
        >
          {getButtonText()}
        </Button>
        {!isDevicesLoading && !isActionLoading && !isUserSupported && (
          <div className="flex items-start gap-1">
            <AlertCircle className="h-4 w-4 text-error-700 mt-0.5" />
            <Type variant="caption" className="text-error-700">
              This browser is not supported. Please try a different one.
            </Type>
          </div>
        )}
        {!isDevicesLoading && !isActionLoading && isUserSupported && showNoSpeakersMessage && (
          <div className="flex flex-col gap-2 p-3 border border-error-300 rounded-md bg-error-50">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-error-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <Type variant="caption" className="text-error-700 font-medium block mb-1">
                  No speaker source selected
                </Type>
                <Type variant="caption" className="text-error-600 block">
                Your browser is most likely using your default speaker. To confirm this click the "Test speakers" button, if you hear the tones, then your audio should be okay in the meeting. If not, please refresh or try a different browser.
                </Type>
              </div>
            </div>
          </div>
        )}
      </div>
      {isErrorDialogOpen && (
        <Dialog
          isOpen
          onClose={(): void => setIsErrorDialogOpen(false)}
          title="Unable to Join Meeting"
          description="There was an issue in joining this meeting. Check your connectivity and try again."
        />
      )}
    </>
  );
};

export default MeetingJoinDetails;
